(ns measure.report-table.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridActionsCellItem]]
            ["src/svg/wpp/content-actions/copy.svg$default" :as Copy]
            ["src/svg/wpp/content-actions/download.svg$default" :as Download]
            ["src/svg/wpp/content-actions/trash.svg$default" :as Trash]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            [cljs-bean.core :refer [->js]]
            [common.flags :refer [use-flag]]
            [common.modal.dialog :refer [deletion-confirmation]]
            [common.modal.view :refer [defmodal]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.resource.query.user :as user-queries]
            [common.util :as util]
            [common.validators.core :refer [valid-date?]]
            [common.widgets.data-grid :as dg]
            [mate.core :as mc]
            [measure.queries :as q]
            [measure.report-builder.context :as report-builder.ctx]
            [measure.report-builder.core :refer [->adset-ids entity-names]]
            [measure.report-builder.view.schedule-report-modal]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def entity-name
  (:report entity-names))

(defmodal confirm-delete :report/confirm-delete
  [{:keys [on-confirm]}]
  [deletion-confirmation
   {:on-submit on-confirm
    :entity-name entity-name}])

(def statuses
  (let [default-color "text.primary"
        success-color "success"
        failed-color "error"]
    {:finished {:label "Finished"
                :icon "done"
                :icon-color success-color}
     :started {:label "Started"
               :icon "sync"
               :icon-color default-color}
     :queued {:label "Queued"
              :icon "pending_actions"
              :icon-color default-color}
     :up_for_retry {:label "Up for retry"
                    :icon "restart_alt"
                    :icon-color default-color}
     :errored {:label "Failed"
               :icon "info"
               :icon-color failed-color}}))

(defn status->content [state row-count hit-row-limit?]
  (cond-> (get statuses (keyword state))
    hit-row-limit?
    (assoc :icon "warning"
           :icon-color "warning"
           :description (str "Your " entity-name " was cut off after 100.000 rows, "
                             (- row-count 100000) " rows were dropped."))))

(defui format-adset
  [{:keys [adsets]}]
  ($ Stack {:direction "row" :overflow "hidden" :spacing 0.5}
     (for [{:keys [adset-name adset-id]} adsets]
       ($ Chip
          {:key adset-id
           :label (str adset-name "(" adset-id ")")}))))

(defui format-dates
  [{:keys [start-date end-date]}]
  (let [format-date (fn [date]
                      (when (and (string? date) (valid-date? date))
                        (util/format-iso-string date "L")))
        start-date (format-date start-date)
        end-date (format-date end-date)]
    (when (or start-date end-date)
      (str start-date " to " end-date))))

(defn- report-finished?
  [report]
  (= (:state report) "finished"))

(defui format-status
  [{:keys [state total-row-count hit-rows-limit]}]
  (let [content (status->content state total-row-count hit-rows-limit)]
    ($ Tooltip
       {:placement :bottom
        :title (:description content)
        :disableHoverListener (nil? (:description content))}
       ($ Stack {:direction "row" :gap 1 :alignItems "center"}
          ($ Icon {:color (:icon-color content)} (:icon content))
          ($ :span (:label content))))))

(defn created-by-filter-options [rows]
  (->> (mc/index-by :user-id rows)
       (map (fn [[id {:keys [user-name]}]]
              {:value id :label user-name}))
       clj->js))

(defn- columns [{:keys [created-by-options adset-id->channel create-report delete-report download-report]}]
  #js [#js {:field "name"
            :headerName "Name"
            :sortable true
            :filterable true
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))
            :flex 2}
       #js {:field "adset"
            :headerName "Adsets"
            :sortable true
            :filterable true
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:adsets row))
            :renderCell (fn [^js params]
                          (let [value (.-value params)]
                            ($ format-adset {:adsets value})))
            :flex 2}
       #js {:field "dateRange"
            :headerName "Date range"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           ((juxt :start-date :end-date) row))
            :renderCell (fn [^js params]
                          (let [[start end] (.-value params)]
                            ($ format-dates {:start-date start
                                             :end-date end})))
            :minWidth 200}
       #js {:field "status"
            :type "singleSelect"
            :headerName "Status"
            :sortable true
            :filterable true
            :valueOptions (clj->js (map (fn [[value {label :label}]]
                                          {:value value :label label})
                                        statuses))
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:state row))
            :renderCell (fn [^js params]
                          (let [row (.-row params)]
                            ($ format-status row)))}
       #js {:field "createdBy"
            :type "singleSelect"
            :headerName "Created by"
            :sortable true
            :filterable true
            :valueOptions created-by-options
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:user-id row))
            :renderCell (fn [^js params]
                          (let [row (.-row params)]
                            (or (:user-name row) "")))
            :flex 1
            :minWidth 120}
       #js {:field "createdAt"
            :type "dateTime"
            :headerName "Created at"
            :sortable true
            :filterable true
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (js/Date. (:created-at row)))
            :renderCell (fn [^js params]
                          (let [value (.-value params)]
                            (util/format-iso-string value)))
            :minWidth 140}
       #js {:field "actions"
            :type "actions"
            :headerName "Actions"
            :cellClassName "hover-action-cell"
            :getActions (fn [^js params]
                          (let [row (.-row params)
                                channel (adset-id->channel (first (->adset-ids row)))
                                unified-report? (= (:type row) "report")]
                            (->> [($ GridActionsCellItem
                                     {:key "download"
                                      :label (str "Download " entity-name)
                                      :icon ($ Download)
                                      :disabled (not (report-finished? row))
                                      :onClick #(download-report {:id (:id row)})
                                      :showInMenu true})
                                  ($ GridActionsCellItem
                                     {:key "delete"
                                      :label (str "Delete " entity-name)
                                      :icon ($ Trash)
                                      :onClick #(rf/dispatch [:modal/open
                                                              {:id :report/confirm-delete
                                                               :props {:on-confirm (fn []
                                                                                     (delete-report {:id (:id row)}))}}])
                                      :showInMenu true})
                                  (when unified-report?
                                    ($ GridActionsCellItem
                                       {:key "use-as-template"
                                        :label "Use as template"
                                        :icon ($ Copy)
                                        :onClick #(create-report
                                                   {:initial-value (-> row
                                                                       (assoc :channel channel)
                                                                       (dissoc :name :id))})
                                        :showInMenu true}))
                                  (when unified-report?
                                    ($ GridActionsCellItem
                                       {:key "schedule"
                                        :label (str "Schedule " entity-name)
                                        :icon ($ Calendar)
                                        :onClick #(rf/dispatch [:modal/open
                                                                {:id :report/schedule
                                                                 :props {:report (-> row
                                                                                     (assoc :channel channel)
                                                                                     (dissoc :name :id))}}])
                                        :showInMenu true}))]
                                 (keep identity)
                                 ->js)))
            :flex 0}])

(defn get-row-id [row]
  (:id row))

(def Toolbar
  (uix/as-react
   (fn [{:keys [on-create]}]
     ($ dg/grid-toolbar
        {:hide-quick-filter true
         :sx #js {:justifyContent "flex-end"}}
        ($ Button
           {:onClick on-create
            :variant "contained"}
           (str "Generate " entity-name))))))

(defui table []
  (let [{:keys [agency-id advertiser-id]} (use-subscribe [:url-context])
        {users :data} (use-query (user-queries/users-query {:agency-id agency-id}))
        {adsets :data} (use-query (adset-queries/adsets-query {:advertiser-id advertiser-id}))

        {:keys [data isFetching]} (use-query (q/reports-query {:advertiser-id advertiser-id}))

        rows (uix/use-memo (fn []
                             (q/enrich-reports data adsets users))
                           [data adsets users])

        created-by-options (uix/use-memo (fn []
                                           (created-by-filter-options rows))
                                         [rows])

        adset-id->channel (uix/use-memo (fn []
                                          (mc/index-by :id :channel adsets))
                                        [adsets])

        {delete-report :mutate} (use-mutation (q/delete-report-mutation {:advertiser-id advertiser-id}))
        {download-report :mutate} (use-mutation (q/download-report-mutation))

        on-row-click (uix/use-callback (fn [^js params]
                                         (let [row (.-row params)]
                                           (when (report-finished? row)
                                             (download-report {:id (:id row)}))))
                                       [download-report])

        unified-reporting-enabled? (use-flag :unified-reporting)
        {:keys [open-report-builder]} (report-builder.ctx/use-report-builder-context)

        on-create (uix/use-callback
                   (fn [& {:keys [initial-value]}]
                     (if unified-reporting-enabled?
                       (open-report-builder {:entity :report
                                             :mode :create
                                             :initial-value initial-value})
                       (rf/dispatch [:form.report/open {:entity :report}])))
                   [unified-reporting-enabled? open-report-builder])]
    ($ DataGridPremium
       {:columns (columns {:created-by-options created-by-options
                           :adset-id->channel adset-id->channel
                           :create-report on-create
                           :delete-report delete-report
                           :download-report download-report})

        :loading isFetching

        :rows (to-array rows)
        :getRowId get-row-id
        :onRowClick on-row-click

        :checkboxSelection false
        :disableRowSelectionOnClick true

        :pagination true
        :pageSizeOptions #js [10]

        :initialState #js {:pagination #js {:paginationModel #js {:pageSize 10}}
                           :sorting #js {:sortModel #js [#js {:field "createdAt" :sort "desc"}]}}
        :slots #js {:toolbar Toolbar}
        :slotProps #js {:toolbar #js {:on-create on-create}
                        :filterPanel #js {:data-testid "filterPanel"
                                          :filterFormProps #js {:data-testid "filterForm"
                                                                :columnInputProps #js {:data-testid "columnInput"}
                                                                :operatorInputProps #js {:data-testid "operatorInput"}
                                                                :valueInputProps #js {:data-testid "valueInput"}}}}
        :sx (util/spread-obj #js {:maxHeight "70rem"
                                  "& .MuiDataGrid-row:hover" #js {:cursor "pointer"}}
                             (->js dg/hover-action-sx))})))
